import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [favIds, setFavIds] = useState(new Set());

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites/ids");
      setFavIds(new Set(data));
    } catch (e) {
      setFavIds(new Set());
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (!["admin", "moderator", "operator"].includes(data.role)) loadFavs();
      return data;
    } catch (e) {
      setUser(false);
      return false;
    }
  }, [loadFavs]);

  useEffect(() => {
    // Skip /me while returning from Google OAuth: AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) return;
    refreshMe();
  }, [refreshMe]);

  const signedIn = (u) => {
    setUser(u);
    if (!["admin", "moderator", "operator"].includes(u.role)) loadFavs();
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    setFavIds(new Set());
  };

  const toggleFav = async (listingId) => {
    const has = favIds.has(listingId);
    const next = new Set(favIds);
    has ? next.delete(listingId) : next.add(listingId);
    setFavIds(next);
    try {
      has ? await api.delete(`/favorites/${listingId}`) : await api.post(`/favorites/${listingId}`);
    } catch (e) {
      setFavIds(favIds);
    }
  };

  return <AuthCtx.Provider value={{ user, setUser, refreshMe, signedIn, logout, favIds, toggleFav }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
